import type { EntityKind } from "~/helpers/search/entitySearchQuery";
import type { SearchResultRow } from "~/layout/useEntitySearch";

import { Box, Chip, Dialog, Icon, IconButton, InputBase } from "@mui/material";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CaptionText } from "~/components/Typographies";
import { COMMAND_PREFIX, replaceEntityQuery } from "~/helpers/search/entitySearchQuery";
import SearchResultsList from "~/layout/SearchResultsList";
import runGuardedExit from "~/layout/unsavedChangesRegistry";
import useCommandCatalog from "~/layout/useCommandCatalog";
import useGlobalSearch from "~/layout/useGlobalSearch";

const TOUCH_ROW_HEIGHT = 48;

const COMMAND_CHIP = "command";

type ActiveChip = EntityKind | typeof COMMAND_CHIP;

const ENTITY_CHIPS: { key: EntityKind; labelKey: string; prefix: string }[] = [
	{ key: "orders", labelKey: "search.hints.orders", prefix: "o" },
	{ key: "customers", labelKey: "search.hints.customers", prefix: "c" },
	{ key: "items", labelKey: "search.hints.items", prefix: "i" }
];

interface GlobalSearchSheetProps {
	onClose: () => void;
	open: boolean;
}

const GlobalSearchSheet = ({ onClose, open }: GlobalSearchSheetProps) => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const inputRef = useRef<HTMLInputElement>(null);
	const [text, setText] = useState("");
	const [activeChip, setActiveChip] = useState<ActiveChip | null>(null);
	const commands = useCommandCatalog();

	const chipPrefix = ENTITY_CHIPS.find((chip) => chip.key === activeChip)?.prefix;
	const effectiveInput =
		activeChip === COMMAND_CHIP ? `${COMMAND_PREFIX}${text}` : chipPrefix ? `${chipPrefix}:${text}` : text;

	const { intent, isLoadingResults, itemSuggestion, navigableRows, query, sections } = useGlobalSearch(
		effectiveInput,
		open,
		commands
	);

	const reset = () => {
		setText("");
		setActiveChip(null);
	};

	const go = (row: SearchResultRow) => {
		onClose();
		if (row.run) {
			row.run();
			return;
		}
		runGuardedExit(() => navigate(row.path, row.state ? { state: row.state } : undefined));
	};

	const handleKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" && navigableRows.length > 0) {
			event.preventDefault();
			go(navigableRows[0]);
		}
	};

	const toggleChip = (kind: ActiveChip) => setActiveChip((current) => (current === kind ? null : kind));

	return (
		<Dialog
			fullScreen
			onClose={onClose}
			open={open}
			slotProps={{
				paper: { sx: { height: "100dvh" } },
				transition: { onEntered: () => inputRef.current?.focus(), onExited: reset }
			}}
		>
			<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
				<Box
					sx={{
						alignItems: "center",
						borderBottom: 1,
						borderColor: "divider",
						display: "flex",
						gap: 0.5,
						pr: 1,
						pt: "env(safe-area-inset-top)"
					}}
				>
					<IconButton aria-label={t("search.mobile.closeAria")} onClick={onClose} sx={{ flexShrink: 0 }}>
						<Icon>arrow_back</Icon>
					</IconButton>
					<InputBase
						autoFocus
						inputProps={{ "aria-label": t("search.inputAria") }}
						inputRef={inputRef}
						onChange={(event) => setText(event.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={t("search.mobile.placeholder")}
						sx={{ flex: 1, fontSize: "1rem", py: 1.5 }}
						value={text}
					/>
					{text && (
						<IconButton aria-label={t("search.clearAria")} onClick={reset} sx={{ flexShrink: 0 }}>
							<Icon>clear</Icon>
						</IconButton>
					)}
				</Box>
				<Box
					sx={{
						alignItems: "center",
						borderBottom: 1,
						borderColor: "divider",
						display: "flex",
						gap: 1,
						px: 1.5,
						py: 1
					}}
				>
					{ENTITY_CHIPS.map((chip) => (
						<Chip
							color={activeChip === chip.key ? "primary" : "default"}
							key={chip.key}
							label={t(chip.labelKey)}
							onClick={() => toggleChip(chip.key)}
							variant={activeChip === chip.key ? "filled" : "outlined"}
						/>
					))}
					<Chip
						color={activeChip === COMMAND_CHIP ? "primary" : "default"}
						icon={<Icon sx={{ fontSize: "1rem" }}>bolt</Icon>}
						label={t("search.hints.commands")}
						onClick={() => toggleChip(COMMAND_CHIP)}
						variant={activeChip === COMMAND_CHIP ? "filled" : "outlined"}
					/>
				</Box>
				<Box sx={{ flex: 1, overflowY: "auto" }}>
					{!query && (
						<CaptionText sx={{ color: "text.secondary", display: "block", px: 2, py: 1.5 }}>
							{t("search.mobile.hint")}
						</CaptionText>
					)}
					<SearchResultsList
						actionRows={[]}
						intent={intent}
						isLoadingResults={isLoadingResults}
						itemSuggestion={itemSuggestion}
						navigableRows={navigableRows}
						onSelectRow={go}
						onSuggestionSelect={(suggestion) => setText(replaceEntityQuery(text, intent, suggestion))}
						query={intent.query}
						rowMinHeight={TOUCH_ROW_HEIGHT}
						sections={sections}
					/>
				</Box>
			</Box>
		</Dialog>
	);
};

export default GlobalSearchSheet;
